import { Box, Grid, Paper } from '@mui/material';
import {
  add,
  differenceInDays,
  getDay,
  getMonth,
  getYear,
  set,
} from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useResizeDetector } from 'react-resize-detector';
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Label,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useRecoilValue } from 'recoil';
import {
  firstDayOfMonth,
  getCircleData,
  getDateText,
  getDayOfWeek,
  getMonthText,
  getStackedBarData,
  getYearDateList,
} from '../../common/helper';
import NoDataPage from '../commonComponent/NoDataPage';
import { SwTitleDownloadMore } from '../commonComponent/SwTitleDownloadMore';
import { SwRwwTab } from '../commonComponent/SwRwwTab';
import { RwwGraphCard } from './RwwGraphCard';
import {
  KEY_CLASS_DAILY_WALK_ACHIEVEMENT_DATA,
  KEY_CLASS_DAILY_WALK_ACHIEVEMENT_RANK,
  KEY_CLASS_DAILY_WRITE_ACHIEVEMENT_DATA,
  KEY_CLASS_DAILY_WRITE_ACHIEVEMENT_RANK,
  KEY_CLASS_MONTHLY_READ_ACHIEVEMENT_DATA,
  KEY_CLASS_MONTHLY_WALK_ACHIEVEMENT_DATA,
  KEY_CLASS_MONTHLY_WRITE_ACHIEVEMENT_DATA,
  KEY_CLASS_READ_ACHIEVEMENT_RANK,
  KEY_CLASS_WALK_ACHIEVEMENT_RANK,
  KEY_CLASS_WRITE_ACHIEVEMENT_RANK,
  KEY_READ_TEXT,
  KEY_WALK_TEXT,
  KEY_WRITE_TEXT,
} from '../../common/key';
import {
  getClassDailyWalkAchievementData,
  getClassDailyWalkAchievementRank,
  getClassDailyWriteAchievementData,
  getClassDailyWriteAchievementRank,
  getClassMonthlyReadAchievementData,
  getClassMonthlyWalkAchievementData,
  getClassMonthlyWriteAchievementData,
  getClassReadAchievementRank,
  getClassWalkAchievementRank,
  getClassWriteAchievementRank,
} from '../../common/api/ApiClassStat';
import { selectSchoolInfoState } from '../../common/atom';
import { useSession } from '../../hooks/session';

interface Props {
  startDate: number;
  from?: string;
  isPdf?: boolean;
}

export const RwwAchievementStatistics: React.FC<Props> = ({
  startDate,
  from = 'dashboard',
  isPdf = false,
}) => {
  const { loginVal } = useSession();
  const schoolClassInfo = useRecoilValue(selectSchoolInfoState);
  const [schoolSeq, setSchoolSeq] = useState<number>(0);
  const [schoolGrade, setSchoolGrade] = useState<string>('1');
  const [schoolClass, setSchoolClass] = useState<string>('1');
  const [reqRequest, setReqRequest] = useState<boolean>(false);
  const [readCircleData, setReadCircleData] = useState<any[]>([]);
  const [walkCircleData, setWalkCircleData] = useState<any[]>([]);
  const [writeCircleData, setWriteCircleData] = useState<any[]>([]);
  const [readStackedBarData, setReadStackedBarData] = useState<any[]>([]);
  const [walkStackedBarData, setWalkStackedBarData] = useState<any[]>([]);
  const [writeStackedBarData, setWriteStackedBarData] = useState<any[]>([]);
  const [readBarLineData, setReadBarLineData] = useState<any[]>([]);
  const [walkBarLineData, setWalkBarLineData] = useState<any[]>([]);
  const [writeBarLineData, setWriteBarLineData] = useState<any[]>([]);
  const [readMultiLineData, setReadMultiLineData] = useState<any[]>([]);
  const [walkMultiLineData, setWalkMultiLineData] = useState<any[]>([]);
  const [writeMultiLineData, setWriteMultiLineData] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [stackedBarWalkDate, setStackedBarWalkDate] = useState<Date>();
  const [stackedBarWriteDate, setStackedBarWriteDate] = useState<Date>();
  const [multiLineWalkDate, setMultiLineWalkDate] = useState<Date>();
  const [multiLineWriteDate, setMultiLineWriteDate] = useState<Date>();
  const [barLineWalkDate, setBarLineWalkDate] = useState<Date>();
  const [barLineWriteDate, setBarLineWriteDate] = useState<Date>();
  const [requestWriteData, setRequestWriteData] = useState<any>();
  const [rowCount, setRowCount] = useState<number>(1);
  const [rateData, setRateData] = useState<any>([]);
  const { width, ref } = useResizeDetector();
  const [loading, setLoading] = useState<any>(false);

  const {
    data,
    isLoading,
    refetch: refetchClassReadAchievementRank,
  } = useQuery(
    KEY_CLASS_READ_ACHIEVEMENT_RANK,
    () => {
      if (reqRequest && schoolClassInfo.schoolSeq !== 0) {
        const newData: any = {
          schoolSeq: schoolClassInfo.schoolSeq,
          schoolGrade: schoolClassInfo.schoolGrade,
          schoolClass: schoolClassInfo.schoolClass,
          year: getYear(startDate * 1000),
          month: getMonthText(startDate * 1000),
        };
        return getClassReadAchievementRank(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null && res.rate !== null) {
          setReadCircleData(getCircleData(res.rate, true, KEY_READ_TEXT));
          setReadStackedBarData(getStackedBarData(res.rate));
        } else {
          setReadCircleData([]);
          setReadStackedBarData([]);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const { refetch: refetchClassWalkAchievementRank } = useQuery(
    KEY_CLASS_WALK_ACHIEVEMENT_RANK,
    () => {
      if (reqRequest && schoolClassInfo.schoolSeq !== 0) {
        const newData: any = {
          schoolSeq: schoolClassInfo.schoolSeq,
          schoolGrade: schoolClassInfo.schoolGrade,
          schoolClass: schoolClassInfo.schoolClass,
          year: getYear(startDate * 1000),
          month: getMonthText(startDate * 1000),
        };
        return getClassWalkAchievementRank(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null && res.rate !== null) {
          setWalkCircleData(getCircleData(res.rate, true, KEY_WALK_TEXT));
        } else {
          setWalkCircleData([]);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const { refetch: refetchClassWriteAchievementRank } = useQuery(
    KEY_CLASS_WRITE_ACHIEVEMENT_RANK,
    () => {
      if (reqRequest && schoolClassInfo.schoolSeq !== 0) {
        const newData: any = {
          schoolSeq: schoolClassInfo.schoolSeq,
          schoolGrade: schoolClassInfo.schoolGrade,
          schoolClass: schoolClassInfo.schoolClass,
          year: getYear(startDate * 1000),
          month: getMonthText(startDate * 1000),
        };
        return getClassWriteAchievementRank(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null && res.rate !== null) {
          setWriteCircleData(getCircleData(res.rate, true, KEY_WRITE_TEXT));
        } else {
          setWriteCircleData([]);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const { refetch: refetchClassDailyWalkAchievementRank } = useQuery(
    KEY_CLASS_DAILY_WALK_ACHIEVEMENT_RANK,
    () => {
      if (
        reqRequest &&
        schoolClassInfo.schoolSeq !== 0 &&
        stackedBarWalkDate !== undefined &&
        !Number.isNaN(stackedBarWalkDate?.getTime())
      ) {
        const newData: any = {
          schoolSeq: schoolClassInfo.schoolSeq,
          schoolGrade: schoolClassInfo.schoolGrade,
          schoolClass: schoolClassInfo.schoolClass,
          year: getYear(stackedBarWalkDate.getTime()),
          month: getMonthText(stackedBarWalkDate.getTime()),
          day: getDateText(stackedBarWalkDate.getTime()),
        };
        return getClassDailyWalkAchievementRank(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null && res.rate !== null) {
          setWalkStackedBarData(getStackedBarData(res.rate));
        } else {
          setWalkStackedBarData([]);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const { refetch: refetchClassDailyWriteAchievementRank } = useQuery(
    KEY_CLASS_DAILY_WRITE_ACHIEVEMENT_RANK,
    () => {
      if (
        reqRequest &&
        schoolClassInfo.schoolSeq !== 0 &&
        stackedBarWriteDate !== undefined &&
        !Number.isNaN(stackedBarWriteDate.getTime())
      ) {
        const newData: any = {
          schoolSeq: schoolClassInfo.schoolSeq,
          schoolGrade: schoolClassInfo.schoolGrade,
          schoolClass: schoolClassInfo.schoolClass,
          year: getYear(stackedBarWriteDate.getTime()),
          month: getMonthText(stackedBarWriteDate.getTime()),
          day: getDateText(stackedBarWriteDate.getTime()),
        };
        return getClassDailyWriteAchievementRank(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null && res.rate !== null) {
          setWriteStackedBarData(getStackedBarData(res.rate));
        } else {
          setWriteStackedBarData([]);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const { refetch: refetchClassMonthlyReadAchievementData } = useQuery(
    KEY_CLASS_MONTHLY_READ_ACHIEVEMENT_DATA,
    () => {
      if (reqRequest && schoolClassInfo.schoolSeq !== 0) {
        const newData: any = {
          schoolSeq: schoolClassInfo.schoolSeq,
          schoolGrade: schoolClassInfo.schoolGrade,
          schoolClass: schoolClassInfo.schoolClass,
          year: getYear(startDate * 1000),
          month: getMonthText(startDate * 1000),
        };
        return getClassMonthlyReadAchievementData(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null && res.rate !== null) {
          const newData =
            res.rate && res.rate.data && JSON.parse(res.rate.data);
          const newBarData = newData.map((item: any, idx: number) => ({
            value: item.v,
            count: item.c,
          }));
          const sortedBarValueData = newData
            .map((item: any, idx: number) => ({
              value: item.v,
              count: item.c,
            }))
            .sort((a: any, b: any) => a.value - b.value);
          const sortedBarData = newData
            .map((item: any, idx: number) => ({
              value: item.v,
              count: item.c,
            }))
            .sort((a: any, b: any) => a.count - b.count);
          setReadBarLineData(sortedBarValueData);
          setReadMultiLineData(sortedBarData);
        } else {
          setReadBarLineData([]);
          setReadMultiLineData([]);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const { refetch: refetchClassDailyWalkAchievementData } = useQuery(
    KEY_CLASS_DAILY_WALK_ACHIEVEMENT_DATA,
    () => {
      if (
        reqRequest &&
        schoolClassInfo.schoolSeq !== 0 &&
        multiLineWalkDate !== undefined &&
        !Number.isNaN(multiLineWalkDate.getTime())
      ) {
        const newData: any = {
          schoolSeq: schoolClassInfo.schoolSeq,
          schoolGrade: schoolClassInfo.schoolGrade,
          schoolClass: schoolClassInfo.schoolClass,
          year: getYear(multiLineWalkDate.getTime()),
          month: getMonthText(multiLineWalkDate.getTime()),
          day: getDateText(multiLineWalkDate.getTime()),
        };
        return getClassDailyWalkAchievementData(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null && res.rate !== null) {
          const newData =
            res.rate && res.rate.data && JSON.parse(res.rate.data);
          const newBarData = newData.map((item: any, idx: number) => ({
            value: item.v,
            count: item.c,
          }));
          const sortedBarValueData = newData
            .map((item: any, idx: number) => ({
              value: item.v,
              count: item.c,
            }))
            .sort((a: any, b: any) => a.value - b.value);
          const sortedBarData = newData
            .map((item: any, idx: number) => ({
              value: item.v,
              count: item.c,
            }))
            .sort((a: any, b: any) => a.count - b.count);
          setWalkBarLineData(sortedBarValueData);
          setWalkMultiLineData(sortedBarData);
        } else {
          setWalkBarLineData([]);
          setWalkMultiLineData([]);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const { refetch: refetchClassDailyWriteAchievementData } = useQuery(
    KEY_CLASS_DAILY_WRITE_ACHIEVEMENT_DATA,
    () => {
      if (
        reqRequest &&
        schoolClassInfo.schoolSeq !== 0 &&
        multiLineWriteDate !== undefined &&
        !Number.isNaN(multiLineWriteDate.getTime())
      ) {
        const newData: any = {
          schoolSeq: schoolClassInfo.schoolSeq,
          schoolGrade: schoolClassInfo.schoolGrade,
          schoolClass: schoolClassInfo.schoolClass,
          year: getYear(multiLineWriteDate.getTime()),
          month: getMonthText(multiLineWriteDate.getTime()),
          day: getDateText(multiLineWriteDate.getTime()),
        };
        return getClassDailyWriteAchievementData(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null && res.rate !== null) {
          const newData =
            res.rate && res.rate.data && JSON.parse(res.rate.data);
          const newBarData = newData.map((item: any, idx: number) => ({
            value: item.v,
            count: item.c,
          }));
          const sortedBarValueData = newData
            .map((item: any, idx: number) => ({
              value: item.v,
              count: item.c,
            }))
            .sort((a: any, b: any) => a.value - b.value);
          const sortedBarData = newData
            .map((item: any, idx: number) => ({
              value: item.v,
              count: item.c,
            }))
            .sort((a: any, b: any) => a.count - b.count);
          setWriteBarLineData(sortedBarValueData);
          setWriteMultiLineData(sortedBarData);
        } else {
          setWriteBarLineData([]);
          setWriteMultiLineData([]);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const reloadData = () => {
    refetchClassReadAchievementRank();
    refetchClassWalkAchievementRank();
    refetchClassWriteAchievementRank();
    refetchClassMonthlyReadAchievementData();
  };

  const reloadDailyData = () => {
    refetchClassDailyWalkAchievementRank();
    refetchClassDailyWriteAchievementRank();
    refetchClassDailyWalkAchievementData();
    refetchClassDailyWriteAchievementData();
  };

  const refreshDailyDate = () => {
    const newDate = firstDayOfMonth(startDate * 1000);
    setStackedBarWalkDate(newDate);
    setStackedBarWriteDate(newDate);
    setMultiLineWalkDate(newDate);
    setMultiLineWriteDate(newDate);
    setBarLineWalkDate(newDate);
    setBarLineWriteDate(newDate);
  };

  useEffect(() => {
    if (schoolClassInfo.schoolSeq !== 0 && reqRequest) {
      reloadData();
      reloadDailyData();
    }
  }, [reqRequest]);

  useEffect(() => {
    if (reqRequest) {
      refetchClassDailyWalkAchievementRank();
    }
  }, [stackedBarWalkDate]);

  useEffect(() => {
    if (reqRequest) {
      refetchClassDailyWriteAchievementRank();
    }
  }, [stackedBarWriteDate]);

  useEffect(() => {
    if (reqRequest) {
      refetchClassDailyWalkAchievementData();
      setBarLineWalkDate(multiLineWalkDate);
    }
  }, [multiLineWalkDate]);

  useEffect(() => {
    if (reqRequest) {
      setMultiLineWalkDate(barLineWalkDate);
    }
  }, [barLineWalkDate]);

  useEffect(() => {
    if (reqRequest) {
      refetchClassDailyWriteAchievementData();
      setBarLineWriteDate(multiLineWriteDate);
    }
  }, [multiLineWriteDate]);

  useEffect(() => {
    if (reqRequest) {
      setMultiLineWriteDate(barLineWriteDate);
    }
  }, [barLineWriteDate]);

  useEffect(() => {
    if (schoolClassInfo.schoolSeq !== 0 && reqRequest) {
      reloadData();
      reloadDailyData();
    }
  }, [schoolSeq, schoolGrade, schoolClass]);

  useEffect(() => {
    if (schoolClassInfo.schoolSeq !== 0) {
      setReqRequest(true);
      reloadData();
      reloadDailyData();
    }
  }, [schoolClassInfo]);

  useEffect(() => {
    refreshDailyDate();
    if (reqRequest) {
      reloadData();
    }
  }, [startDate]);

  useEffect(() => {
    if (reqRequest) {
      reloadData();
    }
  }, [reqRequest]);

  useEffect(() => {
    const currentDate = new Date();
    const currentYear = getYear(currentDate);
    const targetYear = loginVal.value.user.year
      ? loginVal.value.user.year
      : currentYear;
    const newDataList = getYearDateList(targetYear);

    setSelectedDate(
      loginVal.value.user.year === currentYear
        ? new Date(Date.now())
        : new Date(newDataList[0] * 1000)
    );

    if (schoolClassInfo.schoolSeq !== 0) {
      setReqRequest(true);
      reloadData();
      reloadDailyData();
    }
  }, []);

  return (
    <>
      <Grid container spacing={0}>
        <Grid xl={isPdf ? 12 : 4}>
          <RwwGraphCard
            type='circle'
            title='읽기 메달 획득 비율'
            count={0}
            contents={readCircleData}
            startDate={startDate}
            offset={0}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
          />
        </Grid>
        <Grid xl={isPdf ? 12 : 4}>
          <RwwGraphCard
            type='circle'
            title='걷기 메달 획득 비율'
            count={0}
            contents={walkCircleData}
            startDate={startDate}
            offset={100}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
          />
        </Grid>
        <Grid xl={isPdf ? 12 : 4}>
          <RwwGraphCard
            type='circle'
            title='쓰기 메달 획득 비율'
            count={0}
            contents={writeCircleData}
            startDate={startDate}
            offset={200}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
          />
        </Grid>
      </Grid>
      {/* <Grid container spacing={0}>
        <Grid xl={4}>
          <RwwGraphCard
            type='stacked-bar'
            title='읽기 메달 달성률'
            count={0}
            contents={readStackedBarData}
            startDate={startDate}
            offset={0}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
          />
        </Grid>
        <Grid xl={4}>
          <RwwGraphCard
            type='stacked-bar'
            title='걷기 메달 달성률'
            count={0}
            contents={walkStackedBarData}
            startDate={startDate}
            offset={100}
            showDateSelect={true}
            selectedDate={stackedBarWalkDate}
            setSelectedDate={setStackedBarWalkDate}
          />
        </Grid>
        <Grid xl={4}>
          <RwwGraphCard
            type='stacked-bar'
            title='쓰기 메달 달성률'
            count={0}
            contents={writeStackedBarData}
            startDate={startDate}
            offset={200}
            showDateSelect={true}
            selectedDate={stackedBarWriteDate}
            setSelectedDate={setStackedBarWriteDate}
          />
        </Grid>
      </Grid>
      <Grid container spacing={0}>
        <Grid xl={4}>
          <RwwGraphCard
            type='multi-line'
            title='읽기 분포도'
            count={0}
            contents={readMultiLineData}
            startDate={startDate}
            offset={0}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            showStat={true}
            labelTitle={['완독 권수', '학생수']}
          />
        </Grid>
        <Grid xl={4}>
          <RwwGraphCard
            type='multi-line'
            title='걷기 분포도'
            count={0}
            contents={walkMultiLineData}
            startDate={startDate}
            offset={100}
            showDateSelect={true}
            selectedDate={multiLineWalkDate}
            setSelectedDate={setMultiLineWalkDate}
            labelTitle={['걸음수', '학생수']}
          />
        </Grid>
        <Grid xl={4}>
          <RwwGraphCard
            type='multi-line'
            title='쓰기 분포도'
            count={0}
            contents={writeMultiLineData}
            startDate={startDate}
            offset={200}
            showDateSelect={true}
            selectedDate={multiLineWriteDate}
            setSelectedDate={setMultiLineWriteDate}
            labelTitle={['쓰기 횟수', '학생수']}
          />
        </Grid>
      </Grid>
      <Grid container spacing={0}>
        <Grid xl={4}>
          <RwwGraphCard
            type='bar-line'
            title='읽기 분포도'
            count={0}
            contents={readBarLineData}
            startDate={startDate}
            offset={0}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            labelTitle={['완독 권수', '학생수']}
          />
        </Grid>
        <Grid xl={4}>
          <RwwGraphCard
            type='bar-line'
            title='걷기 분포도'
            count={0}
            contents={walkBarLineData}
            startDate={startDate}
            offset={100}
            showDateSelect={true}
            selectedDate={barLineWalkDate}
            setSelectedDate={setBarLineWalkDate}
            labelTitle={['걸음수', '학생수']}
          />
        </Grid>
        <Grid xl={4}>
          <RwwGraphCard
            type='bar-line'
            title='쓰기 분포도'
            count={0}
            contents={writeBarLineData}
            startDate={startDate}
            offset={200}
            showDateSelect={true}
            selectedDate={barLineWriteDate}
            setSelectedDate={setBarLineWriteDate}
            labelTitle={['쓰기 횟수', '학생수']}
          />
        </Grid>
      </Grid> */}
    </>
  );
};
